type Value = Record<string, any> | string
type Schema = {
    id?: string|number
    type: string
    required?: boolean
    items?: Schema[]
    contentType?: string
    multiline?: boolean
    allowMultiple?: boolean
}


const validate = (value: Record<string, any>, schema: Schema) => {
    if (schema.type === 'form' || schema.type === 'container' || schema.type === 'row') {
        // recurse deeper
        return validateForm(value, schema.items)
    } else if (schema.type === 'list') {
        return validateList(value, schema)

    } else if (schema.type === 'media') {
        return validateMedia(value, schema)

    } else if (typeof(schema.id) !== 'undefined') {
        return validateField(value, schema)
    }

    return null
}

const validateMedia = (value: Value, schema: Schema) => {
    if (!schema.required) {
        return null
    }

    if (!value) {
        return 'This field is required'
    }

    let totalCount = 0

    // If value wasn't parsed, then it will be the default array of uploaded id's
    if (Array.isArray(value)) {
        totalCount = value.length
    } else if (typeof value === 'object') {
        totalCount = (value.uploaded?.length || 0) + (value.uploadQueue?.length || 0)
    }

    if (totalCount < 1) {
        return 'At least one item is required'
    }

    if (schema.allowMultiple === false && totalCount > 1) {
        return 'Only one item is allowed'
    }

    return null
}

const validateField = (value: Value, schema: Schema) => {

    // If field is not required and value is undefined/null, it's valid
    if (!schema.required && (value === undefined || value === null || value === '')) {
        return null
    }

    // Required field checks
    if (schema.required) {
        if (value === undefined || value === null) {
            return 'This field is required'
        }

        if (typeof value === 'string' && value.trim() === '') {
            return 'This field cannot be empty'
        }

        if (Array.isArray(value) && value.length === 0) {
            return 'At least one item is required'
        }
    }

    if (schema.type === 'text' && !schema.multiline) {
        if (typeof value !== 'string') {
            return 'Invalid text value'
        }
        switch (schema.contentType) {
            case 'email':
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Invalid email address'
                }
                break
            case 'url':
                if (!/^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([/\w .-]*)*\/?$/.test(value)) {
                    return 'Invalid URL'
                }
                break
            case 'tel': {
                const cleanTel = value.replace(/[\p{Cf}\p{Pd}]/gu, '')
                if (!/^\+?[0-9\s()-]+$/.test(cleanTel)) {
                    return 'Invalid phone number'
                }
                break
            }
            case 'number':
                if (isNaN(Number(value))) {
                    return 'Invalid number'
                }
                break
        }
    }
}

const validateForm = (value: Record<string, any>, schema: Schema[]) => {
    if (!value || !schema) return null
    let errors = {}

    schema.forEach((schema: Schema) => {
        const item = schema.id
            ? {
                [schema.id]: validate(value[schema.id], schema)
            }
            : validate(value, schema)

        errors = {
            ...errors,
            ...item
        }
    })

    return errors
}

const validateList = (listValues: Record<string, any>, listSchema: Schema) => {
    const errors: any[] = []
    // clear out Proxy object stuff
    const newSchema = JSON.parse(JSON.stringify(listSchema))

    if (!newSchema.items && newSchema.item) {
        //maybe backwards compatibility?  Unknown if any schemas exist like this, couldn't find any
        if (Array.isArray(newSchema.item.items)) {
            newSchema.items = newSchema.item.items
        } else {
            newSchema.items = [newSchema.item]
        }
    }

    const itemSchemaLayout = newSchema.items

    if (!listValues || !Array.isArray(listValues) || !itemSchemaLayout || itemSchemaLayout.length === 0) {
        // console.warn(`List schema (id: ${listSchema.id || 'unknown'}) is missing 'items' definition or 'items' is empty.`);
        return errors
    }

    listValues.forEach((itemValue: any) => {
        const itemValidationResult = validateForm( itemValue, itemSchemaLayout )
        errors.push({_id: itemValue._id, ...itemValidationResult})
    })

    return errors
}

const hasErrors = (errors: Record<string, any>): boolean => {
    if (!errors) {
        return false
    }

    if (typeof errors === 'string') {
        return true
    }

    // Handle arrays
    if (Array.isArray(errors)) {
        for (const item of errors) {
            if (hasErrors(item)) {
                return true
            }
        }
        return false
    }

    for (const [, value] of Object.entries(errors)) {
        const result = hasErrors(value)

        if (result) {
            return true
        }
    }

    return false
}

const countErrors = (errors: Record<string, any>): number => {
    if (!errors) {
        return 0
    }

    if (typeof errors === 'string') {
        return 1
    }

    let count = 0

    // Handle arrays
    if (Array.isArray(errors)) {
        for (const item of errors) {
            count += countErrors(item)
        }
        return count
    }

    // Handle objects
    for (const [, value] of Object.entries(errors)) {
        if (typeof value === 'string') {
            count++
        } else {
            count += countErrors(value)
        }
    }

    return count
}

export {
    validate,
    hasErrors,
    countErrors
}
